import type {
	CreateNewsfeedArticleDto,
	UpdateNewsfeedArticleDto,
	UpsertNewsfeedTitleDto,
	SaveNewsfeedArticleResponseDto,
	GetNewsfeedArticleNotificationDto,
	GetNewsfeedDto,
	GetUnreadNewsfeedArticleNotificationsDto,
	MarkNewsfeedArticleSeenDto,
	NewsfeedArticleNotificationDto,
	NewsfeedResponseDto,
} from '@sportspredict/shared';
import api from '@/store/api';

export const newsfeedApi = api.injectEndpoints({
	endpoints: (builder) => ({
		upsertNewsfeedTitle: builder.mutation<void, UpsertNewsfeedTitleDto>({
			query: (body) => ({ url: 'probability/newsfeed/title', method: 'PUT', body }),
			invalidatesTags: (_result, error, { event_id }) =>
				error
					? []
					: [
							{ type: 'Newsfeed', id: event_id },
							{ type: 'ProbabilityEventDetails', id: event_id },
						],
		}),
		createNewsfeedArticle: builder.mutation<
			SaveNewsfeedArticleResponseDto,
			CreateNewsfeedArticleDto
		>({
			query: (body) => ({ url: 'probability/newsfeed/article', method: 'POST', body }),
			invalidatesTags: (_result, error, { event_id }) =>
				error ? [] : [{ type: 'Newsfeed', id: event_id }],
		}),
		updateNewsfeedArticle: builder.mutation<
			SaveNewsfeedArticleResponseDto,
			UpdateNewsfeedArticleDto & { id: string; event_id: string }
		>({
			query: ({ id, ...body }) => ({
				url: `probability/newsfeed/article/${id}`,
				method: 'PUT',
				body,
			}),
			invalidatesTags: (_result, error, { event_id }) =>
				error ? [] : [{ type: 'Newsfeed', id: event_id }],
		}),
		deleteNewsfeedArticle: builder.mutation<void, { id: string; event_id: string }>({
			query: ({ id }) => ({ url: `probability/newsfeed/article/${id}`, method: 'DELETE' }),
			invalidatesTags: (_result, error, { event_id }) =>
				error ? [] : [{ type: 'Newsfeed', id: event_id }],
		}),
		getNewsfeed: builder.query<NewsfeedResponseDto, GetNewsfeedDto>({
			query: ({ event_id }) => ({
				url: 'probability/newsfeed',
				method: 'GET',
				params: { event_id },
			}),
			extraOptions: { skipAuth: true },
			providesTags: (_result, _error, { event_id }) => [{ type: 'Newsfeed', id: event_id }],
			keepUnusedDataFor: 300,
		}),
		getNewsfeedArticleNotification: builder.query<
			NewsfeedArticleNotificationDto | null,
			GetNewsfeedArticleNotificationDto
		>({
			query: ({ article_id }) => ({
				url: 'probability/newsfeed/article/notification',
				method: 'GET',
				params: { article_id },
			}),
		}),
		getUnreadNewsfeedArticleNotifications: builder.query<
			NewsfeedArticleNotificationDto[],
			GetUnreadNewsfeedArticleNotificationsDto
		>({
			query: ({ event_id }) => ({
				url: 'probability/newsfeed/article/notifications',
				method: 'GET',
				params: { event_id },
			}),
			keepUnusedDataFor: 300,
		}),
		markNewsfeedArticleSeen: builder.mutation<void, MarkNewsfeedArticleSeenDto>({
			query: (body) => ({
				url: 'probability/newsfeed/article/notification/seen',
				method: 'PUT',
				body,
			}),
		}),
	}),
	overrideExisting: false,
});

export const {
	useUpsertNewsfeedTitleMutation,
	useCreateNewsfeedArticleMutation,
	useUpdateNewsfeedArticleMutation,
	useDeleteNewsfeedArticleMutation,
	useGetNewsfeedQuery,
	useLazyGetNewsfeedArticleNotificationQuery,
	useGetUnreadNewsfeedArticleNotificationsQuery,
	useLazyGetUnreadNewsfeedArticleNotificationsQuery,
	useMarkNewsfeedArticleSeenMutation,
} = newsfeedApi;

export default newsfeedApi;
